import React, { useState, useRef, DragEvent } from 'react';
import { Upload, Link, X, Loader2, FileText } from 'lucide-react';
import { baseApi } from '../../api/baseClient';

interface ImageUploadProps {
  initialImageUrl?: string;
  onImageChange: (url: string) => void;
  title?: string;
  description?: string;
  accept?: string;
  maxSizeMB?: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  initialImageUrl,
  onImageChange,
  title = 'Archivo',
  description = 'Sube o arrastra un archivo, o ingresa una URL',
  accept = 'image/jpeg,image/png,image/svg+xml,image/webp',
  maxSizeMB = 5
}) => {
  const [fileUrl, setFileUrl] = useState(initialImageUrl || '');
  const [useExternalUrl, setUseExternalUrl] = useState(!!initialImageUrl?.startsWith('http'));
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getValidTypes = () => {
    return accept.split(',').map(t => t.trim());
  };

  const handleFileUpload = async (file: File) => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const validTypes = getValidTypes();
      if (!validTypes.includes(file.type)) {
        const friendly = validTypes.map(t => t.split('/')[1]?.toUpperCase()).join(', ');
        throw new Error('Formato invalido. Usa: ' + friendly);
      }

      const maxSize = maxSizeMB * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('El archivo no debe superar los ' + maxSizeMB + 'MB.');
      }

      const formData = new FormData();
      formData.append('image', file);

      // Token de subida de corta vida: lo emite lealify-base con la sesión del
      // operador (cookie). Ya NO se hornea ninguna key estática en el bundle.
      const { token } = await baseApi.get<{ token: string }>('/uploads/token');

      const response = await fetch('https://api.lealify.com/upload', {
        method: 'POST',
        headers: { 'x-upload-token': token },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Error ' + response.status + ' al subir');
      }

      if (!data.success || !data.url) {
        throw new Error(data?.error || 'Respuesta invalida del servidor');
      }

      const fullUrl = data.url.startsWith('http') ? data.url : 'https://api.lealify.com' + data.url;
      setFileUrl(fullUrl);
      setIsPdf(file.type === 'application/pdf');
      onImageChange(fullUrl);
      setUseExternalUrl(false);

    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      console.error('Error al subir:', error);
      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFileUrl(url);
    setIsPdf(url.toLowerCase().endsWith('.pdf'));
    setErrorMsg(null);
    onImageChange(url);
  };

  const handleRemove = () => {
    setFileUrl('');
    setErrorMsg(null);
    setIsPdf(false);
    onImageChange('');
    setUseExternalUrl(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setErrorMsg(null);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const isFilePdf = isPdf || fileUrl.toLowerCase().endsWith('.pdf');

  const getDropZoneClass = () => {
    let base = 'mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors';
    if (isDragging) {
      base += ' border-blue-500 bg-blue-50 dark:bg-blue-500/10';
    } else {
      base += ' border-line';
    }
    if (errorMsg) {
      base += ' border-red-400 bg-red-50 dark:bg-red-500/10';
    }
    return base;
  };

  const getLabelClass = () => {
    let base = 'relative cursor-pointer bg-surface rounded-md font-medium text-accent hover:text-blue-500';
    if (loading) {
      base += ' opacity-50 cursor-not-allowed';
    }
    return base;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-content-2">{title}</label>
        <button
          type="button"
          onClick={() => {
            setUseExternalUrl(!useExternalUrl);
            setErrorMsg(null);
          }}
          className="text-sm text-accent hover:text-blue-500"
        >
          {useExternalUrl ? 'Subir archivo' : 'Usar URL externa'}
        </button>
      </div>
      <p className="text-xs text-content-3 mb-2">{description}</p>

      {useExternalUrl && (
        <div className="mt-1">
          <div className="flex gap-2 items-center">
            <Link className="h-4 w-4 text-content-3 flex-shrink-0" />
            <input
              type="url"
              value={fileUrl}
              onChange={handleUrlChange}
              placeholder="https://ejemplo.com/comprobante.jpg"
              className="block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
            />
          </div>
        </div>
      )}

      {!useExternalUrl && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={getDropZoneClass()}
        >
          <div className="space-y-1 text-center">
            {loading ? (
              <Loader2 className="mx-auto h-12 w-12 text-content-3 animate-spin" />
            ) : (
              <Upload className="mx-auto h-12 w-12 text-content-3" />
            )}

            <div className="flex text-sm text-content-2 justify-center">
              <label htmlFor="file-upload" className={getLabelClass()}>
                <span>Subir archivo</span>
                <input
                  id="file-upload"
                  type="file"
                  ref={fileInputRef}
                  className="sr-only"
                  accept={accept}
                  onChange={handleFileChange}
                  disabled={loading}
                />
              </label>
              <p className="pl-1">o arrastra y suelta</p>
            </div>
            <p className="text-xs text-content-3">Hasta {maxSizeMB}MB</p>
            {errorMsg && !loading && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errorMsg}</p>
            )}
            {loading && (
              <p className="text-xs text-accent mt-1">Subiendo...</p>
            )}
          </div>
        </div>
      )}

      {fileUrl && (
        <div className="mt-4">
          <p className="text-xs text-content-3 mb-1">Vista previa:</p>
          <div className="relative inline-block group">
            {isFilePdf ? (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 bg-canvas rounded-lg border border-line hover:bg-surface-2 transition-colors"
              >
                <FileText className="h-8 w-8 text-red-500" />
                <span className="text-sm text-content-2">Ver PDF</span>
              </a>
            ) : (
              <img
                src={fileUrl}
                alt="Preview"
                onError={() => {
                  setErrorMsg('No se pudo cargar la imagen.');
                  setFileUrl('');
                  onImageChange('');
                }}
                className="h-32 w-auto max-w-xs object-contain rounded-lg border border-line bg-canvas"
              />
            )}
            <button
              type="button"
              onClick={handleRemove}
              className="hover-reveal absolute -top-2 -right-2 bg-red-100 dark:bg-red-500/20 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-200"
              title="Quitar"
            >
              <X className="h-4 w-4 text-red-600 dark:text-red-400" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;